/**
 * Pricing — "¿a qué precio EXACTO pongo cada item de mi hangar?".
 *
 *  - Summary tiles (total a vender, items con ladder local, qty ya listada).
 *  - Por item: ladder (top 5 sell orders locales) + 3 sugerencias
 *    (undercut_1 / match / undercut_5pct) con revenue estimada + queue
 *    depth. Para venta rápida (5min-1h) → undercut_1; para no regalar
 *    margin → match; balance → undercut_5pct.
 *  - Toggle "ya listadas" (filtra items con existing orders en 0).
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'

type BoardEntry = {
  type_id: number
  name: string
  quantity: number
  ladder: Array<{ price: number; volume_remain: number }>
  best_local_sell: { price: number; volume_remain: number } | null
  jita_sell: number | null
  suggestions: {
    undercut_1: { price: number; queue_depth: number }
    match: { price: number; queue_depth: number }
    undercut_5pct: { price: number; queue_depth: number } | null
  }
  your_existing_orders: number
  revenue_at: {
    undercut_1: number | null
    match: number | null
    undercut_5pct: number | null
  }
  note?: string
}

type SuggestionKey = 'undercut_1' | 'match' | 'undercut_5pct'

export function Pricing() {
  const { key: forceRefetchKey, force, bump } = useForceRefetch()
  const [hideListed, setHideListed] = useState(false)
  const [search, setSearch] = useState('')

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['utility-pricing-board', forceRefetchKey],
    queryFn: () => api.pricingBoard(force),
  })

  const items: BoardEntry[] = data?.items ?? []
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((it) => {
      if (hideListed && it.your_existing_orders > 0) return false
      if (q && !it.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [items, hideListed, search])

  const s = data?.summary
  const loc = data?.location
  const inSpace = loc?.kind === 'in_space'
  const totalQtyAll = items.reduce((a, i) => a + i.quantity, 0)

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Pricing</h1>
          <p className="text-eve-muted text-sm">
            "¿A cuánto pongo cada item hoy?" — ladder del order book local +
            3 sugerencias automáticas con revenue estimada.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Summary tiles */}
      {s && !inSpace && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryTile
            label="Items analizados"
            value={String(s.total_items_analyzed)}
            sub={s.total_items_analyzed > 0
              ? `${totalQtyAll.toLocaleString('en-US')} u en hangar`
              : '—'}
          />
          <SummaryTile
            label="Con ladder local"
            value={String(s.items_with_ladder)}
            sub={
              s.local_market_available
                ? `${s.total_items_analyzed - s.items_with_ladder} sin mercado`
                : 'sin market acá'
            }
            accent={s.items_with_ladder > 0}
          />
          <SummaryTile
            label="Location"
            value={
              loc?.kind === 'structure'
                ? `Structure ${loc.structure_id ?? '—'}`
                : loc?.kind === 'station'
                  ? `Station ${loc.station_id ?? '—'}`
                  : '—'
            }
            sub={loc?.region_id ? `Region ${loc.region_id}` : '—'}
          />
          <SummaryTile
            label="Omitidos"
            value={String(data?.skipped ?? 0)}
            sub="items fuera del top 20"
            negative={(data?.skipped ?? 0) > 0}
          />
        </div>
      )}

      {/* Filter bar */}
      <div className="card">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar item…"
            className="bg-eve-panel border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text placeholder:text-eve-muted focus:border-eve-accent focus:outline-none w-full md:w-64"
          />
          <label className="text-xs text-eve-muted flex items-center gap-2">
            <input
              type="checkbox"
              checked={hideListed}
              onChange={(e) => setHideListed(e.target.checked)}
              className="rounded border-eve-border"
            />
            Ocultar ya listadas
          </label>
          <span className="text-eve-muted text-xs ml-auto">
            {filtered.length} / {items.length}
          </span>
        </div>

        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : inSpace ? (
          <div className="text-eve-muted py-8 text-center italic">
            Estás en space — volve a dockear para leer el order book local.
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-eve-muted py-8 text-center">
            {items.length === 0
              ? 'No tenés items en el hangar dockeado.'
              : 'Sin matches para el filtro.'}
          </div>
        ) : (
          <div className="space-y-2 mt-4">
            {filtered.map((it) => (
              <BoardRow key={it.type_id} item={it} />
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-eve-muted">
        <strong>Undercut 1</strong> = top local − 0.01 (venta rápida).{' '}
        <strong>Match</strong> = mismo precio que el top (sin regalar).{' '}
        <strong>Undercut 5%</strong> = Jita sell − 5% cap al top local. KV
        cachea orders 5min.
      </p>
    </div>
  )
}

function SummaryTile({
  label,
  value,
  sub,
  accent,
  negative,
}: {
  label: string
  value: string
  sub?: string
  accent?: boolean
  negative?: boolean
}) {
  const cls = accent
    ? 'text-eve-accent'
    : negative
      ? 'text-eve-danger'
      : 'text-eve-text'
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${cls}`}>{value}</div>
      {sub && <div className="text-xs text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

function BoardRow({ item }: { item: BoardEntry }) {
  return (
    <details
      open
      className="border border-eve-border rounded-md bg-eve-bg/40"
    >
      <summary className="cursor-pointer p-3 hover:bg-eve-panel/50 transition-colors">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium">{item.name}</span>
              <span className="text-xs text-eve-muted font-mono">
                × {item.quantity.toLocaleString('en-US')}
              </span>
              {item.your_existing_orders > 0 && (
                <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-eve-accent/10 border border-eve-accent/30 text-eve-accent">
                  {item.your_existing_orders} ya listada
                  {item.your_existing_orders === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {item.note && (
              <div className="text-xs text-eve-warn mt-1">{item.note}</div>
            )}
          </div>
          <SuggestionGrid item={item} />
        </div>
      </summary>

      <div className="border-t border-eve-border px-3 py-3 text-xs space-y-3">
        {/* Ladder */}
        <div>
          <div className="stat-label mb-2">
            Ladder local (top {item.ladder.length})
          </div>
          {item.ladder.length === 0 ? (
            <div className="text-eve-muted italic">Sin órdenes locales.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-eve-muted uppercase tracking-wider">
                  <th className="py-1 font-medium">#</th>
                  <th className="py-1 font-medium text-right">Precio</th>
                  <th className="py-1 font-medium text-right">Qty aún</th>
                </tr>
              </thead>
              <tbody>
                {item.ladder.map((row, i) => (
                  <tr
                    key={i}
                    className={`border-t border-eve-border/40 ${
                      i === 0 ? 'text-eve-accent font-medium' : ''
                    }`}
                  >
                    <td className="py-1">{i === 0 ? 'top' : i + 1}</td>
                    <td className="py-1 text-right font-mono">
                      {formatISK(row.price, 2)}
                    </td>
                    <td className="py-1 text-right font-mono">
                      {row.volume_remain.toLocaleString('en-US')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Suggestions detail */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SuggestionDetail
            label="Undercut 1"
            sub="Venta rápida"
            sug={item.suggestions.undercut_1}
            revenue={item.revenue_at.undercut_1}
            tone="success"
          />
          <SuggestionDetail
            label="Match"
            sub="Sin regalar"
            sug={item.suggestions.match}
            revenue={item.revenue_at.match}
            tone="muted"
          />
          <SuggestionDetail
            label="Undercut 5%"
            sub="Jita sell − 5%"
            sug={item.suggestions.undercut_5pct}
            revenue={item.revenue_at.undercut_5pct}
            tone="accent"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-1 border-t border-eve-border/40">
          <Row label="Jita sell ref" value={fmt(item.jita_sell)} />
          <Row
            label="Best local"
            value={
              item.best_local_sell
                ? `${formatISK(item.best_local_sell.price, 2)} (${item.best_local_sell.volume_remain.toLocaleString('en-US')} u)`
                : '—'
            }
          />
          <Row
            label="Tus orders activas"
            value={String(item.your_existing_orders)}
          />
        </div>
      </div>
    </details>
  )
}

function SuggestionGrid({ item }: { item: BoardEntry }) {
  // 3-mini-block in summary line
  const rows: Array<{
    key: SuggestionKey
    label: string
    tone: 'success' | 'muted' | 'accent'
  }> = [
    { key: 'undercut_1', label: 'Undercut 1', tone: 'success' },
    { key: 'match', label: 'Match', tone: 'muted' },
    { key: 'undercut_5pct', label: '−5%', tone: 'accent' },
  ]
  return (
    <div className="flex items-center gap-2">
      {rows.map((r) => {
        const sug = item.suggestions[r.key]
        const price = sug?.price ?? null
        const toneCls =
          r.tone === 'success'
            ? 'text-eve-success'
            : r.tone === 'accent'
              ? 'text-eve-accent'
              : 'text-eve-muted'
        return (
          <div
            key={r.key}
            className="bg-eve-panel/50 border border-eve-border rounded px-2 py-1 min-w-[88px] text-right"
          >
            <div className="text-[9px] uppercase text-eve-muted font-mono">
              {r.label}
            </div>
            <div className={`text-xs font-mono font-semibold ${toneCls}`}>
              {price && price > 0 ? formatISK(price, 2) : '—'}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SuggestionDetail({
  label,
  sub,
  sug,
  revenue,
  tone,
}: {
  label: string
  sub: string
  sug: { price: number; queue_depth: number } | null
  revenue: number | null
  tone: 'success' | 'muted' | 'accent'
}) {
  const toneCls =
    tone === 'success'
      ? 'border-eve-success/40'
      : tone === 'accent'
        ? 'border-eve-accent/40'
        : 'border-eve-border'
  return (
    <div className={`border ${toneCls} rounded p-2 bg-eve-panel/40`}>
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase font-mono text-eve-muted">
          {label}
        </div>
        <div className="text-[10px] text-eve-muted italic">{sub}</div>
      </div>
      {sug && sug.price > 0 ? (
        <>
          <div className="font-mono text-sm font-semibold mt-0.5">
            {formatISK(sug.price, 2)}
          </div>
          <div className="text-[10px] text-eve-muted mt-0.5">
            depth {sug.queue_depth} · revenue {revenue !== null ? formatISKShort(revenue) : '—'} ISK
          </div>
        </>
      ) : (
        <div className="text-xs text-eve-muted italic mt-0.5">—</div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-eve-muted">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

function fmt(n: number | null): string {
  return n !== null ? formatISK(n, 2) : '—'
}
